(() => {
  const ROOT_SELECTOR = "[data-tiered-discount-root]";
  const LOCAL_LINE_ID = "__local__";
  const DEFAULT_VISIBLE_DETAIL_COUNT = 2;
  // Hover/click divide for milestone tooltips: below 1440px (mobile + tablet)
  // taps pin the tooltip; from 1440px up it is hover/keyboard-focus only.
  const MOBILE_TOOLTIP_MEDIA_QUERY = "(max-width: 1439px)";
  const {
    parseInteger: parseNumber,
    parseJson,
    normalizeCampaigns,
    getCurrentTier,
    getTierThreshold,
    getExtendedProgressMax,
    getNextTier,
    optimizeDiscountAssignments,
    resolveProductDisplayCampaign,
  } = window.ElegooTieredDiscountUtils || {};

  const sectionRefreshRequests = new Map();
  let cartStateVersion = 0;

  const parseJsonScript = (root, selector, fallback) => {
    const script = root.querySelector(selector);
    if (!script) return fallback;
    return parseJson(script.textContent || "", fallback);
  };

  const getRootRefreshConfig = (root) => {
    const dataNode = root.querySelector("[data-tiered-discount-data]");
    if (!dataNode) return null;
    const sectionId = String(dataNode.dataset.tieredDiscountSectionId || "").trim();
    const refreshUrl = String(dataNode.dataset.tieredDiscountRefreshUrl || "").trim();
    if (!sectionId || !refreshUrl) return null;
    return { dataNode, sectionId, refreshUrl };
  };

  const applyLatestPayload = (root, sourceRoot) => {
    if (!root || !sourceRoot) return;
    const currentDataNode = root.querySelector("[data-tiered-discount-data]");
    const currentCampaigns = root.querySelector("[data-tiered-discount-campaigns]");
    const currentCartLines = root.querySelector("[data-tiered-discount-cart-lines]");
    const nextDataNode = sourceRoot.querySelector("[data-tiered-discount-data]");
    const nextCampaigns = sourceRoot.querySelector("[data-tiered-discount-campaigns]");
    const nextCartLines = sourceRoot.querySelector("[data-tiered-discount-cart-lines]");

    if (currentDataNode && nextDataNode) {
      Array.from(nextDataNode.attributes).forEach((attribute) => {
        currentDataNode.setAttribute(attribute.name, attribute.value);
      });
    }
    if (currentCampaigns && nextCampaigns) {
      currentCampaigns.textContent = nextCampaigns.textContent || "";
    }
    if (currentCartLines && nextCartLines) {
      currentCartLines.textContent = nextCartLines.textContent || "";
    }
  };

  const shouldRefreshRootPayload = (root, config) => {
    const dataNode = config?.dataNode || root.querySelector("[data-tiered-discount-data]");
    if (!dataNode) return false;
    const syncedVersion = parseNumber(dataNode.dataset.tieredDiscountCartVersionSynced, 0);
    return syncedVersion < cartStateVersion;
  };

  const markRootPayloadSynced = (root) => {
    const dataNode = root.querySelector("[data-tiered-discount-data]");
    if (!dataNode) return;
    dataNode.dataset.tieredDiscountCartVersionSynced = String(cartStateVersion);
  };

  const markCartChanged = () => {
    cartStateVersion += 1;
  };

  const fetchLatestRootPayload = async (root) => {
    const config = getRootRefreshConfig(root);
    if (!config) return root;
    if (!shouldRefreshRootPayload(root, config)) return root;

    const requestKey = `${config.refreshUrl}::${config.sectionId}`;
    if (!sectionRefreshRequests.has(requestKey)) {
      const fetchUrl = new URL(config.refreshUrl, window.location.origin);
      fetchUrl.searchParams.set("section_id", config.sectionId);
      sectionRefreshRequests.set(
        requestKey,
        fetch(fetchUrl.toString(), {
          headers: {
            Accept: "text/html",
          },
        })
          .then((response) => {
            if (!response.ok) {
              throw new Error(`Failed to refresh tiered discount: ${response.status}`);
            }
            return response.text();
          })
          .then((htmlText) => {
            const doc = new DOMParser().parseFromString(htmlText, "text/html");
            return doc.getElementById(root.id) || doc.querySelector(`#${CSS.escape(root.id)}`);
          })
          .finally(() => {
            sectionRefreshRequests.delete(requestKey);
          }),
      );
    }

    try {
      const latestRoot = await sectionRefreshRequests.get(requestKey);
      if (latestRoot) {
        applyLatestPayload(root, latestRoot);
        markRootPayloadSynced(root);
      }
    } catch (error) {
      console.warn(error);
    }

    return root;
  };

  const htmlEntityDecoder = document.createElement("textarea");
  const decodeHtmlEntities = (value) => {
    const input = String(value ?? "");
    if (!input || !input.includes("&")) return input;
    htmlEntityDecoder.innerHTML = input;
    return htmlEntityDecoder.value;
  };

  function formatMoneyTiered(cents) {
    let format = window.theme.settings.money_with_currency_format || "${{amount}}";
    return formatMoney(cents, format);
  }

  const normalizeLines = (rawLines) =>
    (Array.isArray(rawLines) ? rawLines : [])
      .map((line) => ({
        id: String(line?.id || "").trim(),
        quantity: parseNumber(line?.quantity, 0),
        subtotalCents: parseNumber(line?.subtotalCents, 0),
        lineSubtotalCents: parseNumber(line?.lineSubtotalCents, 0),
        title: String(line?.title || "").trim(),
        productUrl: String(line?.productUrl || "").trim(),
        imageSrc: String(line?.imageSrc || "").trim(),
        imageAlt: String(line?.imageAlt || "").trim(),
        variantTitle: String(line?.variantTitle || "").trim(),
        unitPriceCents: parseNumber(line?.unitPriceCents, 0),
        finalUnitPriceCents: parseNumber(line?.finalUnitPriceCents, 0),
        campaignIds: Array.isArray(line?.campaignIds)
          ? line.campaignIds
              .map((discountId) => String(discountId || "").trim())
              .filter(Boolean)
          : [],
      }))
      .filter(
        (line) =>
          line.id && line.quantity > 0 && line.subtotalCents >= 0 && line.campaignIds.length,
      );

  const getProgressRatio = (tiers, basis, metric, options = {}) => {
    if (!tiers.length) return 0;
    const max =
      options.maxOverride != null
        ? parseNumber(options.maxOverride, 0)
        : getTierThreshold(tiers[tiers.length - 1], basis);
    if (!max) return 0;
    return Math.max(0, Math.min(1, metric / max));
  };

  const formatTemplate = (template, replacements) => {
    return Object.entries(replacements).reduce(
      (result, [key, value]) => result.replace(`{{ ${key} }}`, value),
      template
    );
  };

  const getUnitLabel = (basis, remainingLabel, itemLabelSingular, itemLabelPlural) => {
    if (basis === "amount") return "";
    const count = parseNumber(remainingLabel || 0, 0);
    const label = count === 1 ? (itemLabelSingular || "") : (itemLabelPlural || "");
    return ` ${label}`.trimStart();
  };

  const normalizeValueType = (valueType) =>
    String(valueType || "").toLowerCase() === "amount" ? "amount" : "percentage";

  const normalizeMessageBasis = (basis) =>
    String(basis || "").toLowerCase() === "amount" ? "amount" : "quantity";

  const getMetricDisplayData = ({
    basis,
    metric,
    itemLabelSingular,
    itemLabelPlural,
  }) => {
    const normalizedBasis = normalizeMessageBasis(basis);
    const normalizedMetric = Math.max(0, parseNumber(metric, 0));
    const value = normalizedBasis === "amount" ? formatMoneyTiered(normalizedMetric) : `${normalizedMetric}`;
    return {
      value,
      unit: getUnitLabel(
        normalizedBasis,
        value,
        itemLabelSingular,
        itemLabelPlural,
      ),
    };
  };

  const getRemainingDisplayData = ({
    basis,
    metric,
    nextTier,
    itemLabelSingular,
    itemLabelPlural,
  }) => {
    if (!nextTier) {
      return {
        value: "",
        remainingValue: "",
        unit: "",
      };
    }
    const normalizedBasis = normalizeMessageBasis(basis);
    const nextThreshold = getTierThreshold(nextTier, normalizedBasis);
    const normalizedMetric = Math.max(0, parseNumber(metric, 0));
    const remaining = Math.max(nextThreshold - normalizedMetric, 0);
    const remainingDisplayData = getMetricDisplayData({
      basis: normalizedBasis,
      metric: remaining,
      itemLabelSingular,
      itemLabelPlural,
    });
    const nextThresholdDisplayData = getMetricDisplayData({
      basis: normalizedBasis,
      metric: nextThreshold,
      itemLabelSingular,
      itemLabelPlural,
    });
    return {
      value: remainingDisplayData.value || nextThresholdDisplayData.value,
      remainingValue: remainingDisplayData.value,
      unit: remainingDisplayData.unit,
    };
  };

  const getTierOffValue = (valueType, tier, { requirePositive = false } = {}) => {
    const normalizedValueType = normalizeValueType(valueType);
    if (normalizedValueType === "amount") {
      const amountOffCents = parseNumber(tier?.amountOffCents, 0);
      if (requirePositive && amountOffCents <= 0) return "";
      return formatMoneyTiered(amountOffCents);
    }
    const percent = parseNumber(tier?.percent, 0);
    if (requirePositive && percent <= 0) return "";
    return `${percent}%`;
  };

  const getTierTransitionData = (valueType, currentTier, nextTier) => {
    const currentOffValue = getTierOffValue(valueType, currentTier, {
      requirePositive: true,
    });
    return {
      currentOffValue,
      nextOffValue: nextTier ? getTierOffValue(valueType, nextTier) : "",
    };
  };

  const formatTierMessage = ({
    compact = false,
    addTemplate,
    unlockedTemplate,
    upgradeTemplate,
    unlockedOffValue,
    transitionData,
    remainingData,
    extraTemplateData = {},
  }) => {
    if (!transitionData.nextOffValue) {
      if (!unlockedTemplate || !unlockedOffValue) return "";
      return formatTemplate(unlockedTemplate, {
        off_value: unlockedOffValue,
        ...extraTemplateData,
      });
    }

    const sharedTemplateData = {
      value: remainingData.value,
      unit: remainingData.unit,
      off_value: transitionData.nextOffValue,
      next_off_value: transitionData.nextOffValue,
      ...extraTemplateData,
    };

    if (!transitionData.currentOffValue) {
      if (!addTemplate) return "";
      return formatTemplate(addTemplate, sharedTemplateData);
    }

    if (!upgradeTemplate) return "";
    const upgradeData = {
      ...sharedTemplateData,
      current_off_value: transitionData.currentOffValue,
    };
    return compact
      ? `${transitionData.currentOffValue} · ${formatTemplate(upgradeTemplate, upgradeData)}`
      : formatTemplate(upgradeTemplate, upgradeData);
  };

  const getMessage = ({
    valueType,
    basis,
    compact,
    addTemplate,
    unlockedTemplate,
    upgradeTemplate,
    itemLabelSingular,
    itemLabelPlural,
    metric,
    currentTier,
    nextTier,
  }) => {
    const transitionData = getTierTransitionData(valueType, currentTier, nextTier);

    if (!nextTier) {
      return formatTierMessage({
        compact,
        unlockedTemplate,
        unlockedOffValue: transitionData.currentOffValue,
        transitionData,
        remainingData: { value: "", unit: "" },
      });
    }

    const remainingData = getRemainingDisplayData({
      basis,
      metric,
      nextTier,
      itemLabelSingular,
      itemLabelPlural,
    });
    return formatTierMessage({
      compact,
      addTemplate,
      upgradeTemplate,
      transitionData,
      remainingData: {
        ...remainingData,
        value: metric <= 0 ? remainingData.value : remainingData.remainingValue,
      },
    });
  };

  const getPdpMessage = ({
    context,
    valueType,
    basis,
    addTemplate,
    unlockedTemplate,
    upgradeTemplate,
    pdpTemplates,
    cartMetric,
    cartCount,
    itemLabelSingular,
    itemLabelPlural,
    totalMetric,
    totalCurrentTier,
    totalNextTier,
    cartCurrentTier,
    cartNextTier,
  }) => {
    if (context !== "product") return "";

    const cartMetricValue = Math.max(0, parseNumber(cartMetric, 0));
    const cartItemCount = Math.max(0, parseNumber(cartCount, 0));
    const cartDisplayData = getMetricDisplayData({
      basis,
      metric: normalizeMessageBasis(basis) === "amount" ? cartMetricValue : cartItemCount,
      itemLabelSingular,
      itemLabelPlural,
    });
    const isFreshScenario = cartMetricValue <= 0;
    const resolvedAddTemplate = isFreshScenario ? addTemplate : pdpTemplates.cartAdd;
    const resolvedUnlockedTemplate = isFreshScenario
      ? unlockedTemplate
      : pdpTemplates.cartMax;
    const resolvedUpgradeTemplate = isFreshScenario
      ? upgradeTemplate
      : pdpTemplates.cartUpgrade;
    const activeMetric = isFreshScenario ? totalMetric : cartMetricValue;
    const activeCurrentTier = isFreshScenario ? totalCurrentTier : cartCurrentTier;
    const activeNextTier = isFreshScenario ? totalNextTier : cartNextTier;
    const transitionData = getTierTransitionData(
      valueType,
      activeCurrentTier,
      activeNextTier,
    );

    const remainingData = getRemainingDisplayData({
      basis,
      metric: activeMetric,
      nextTier: activeNextTier,
      itemLabelSingular,
      itemLabelPlural,
    });
    return formatTierMessage({
      addTemplate: resolvedAddTemplate,
      unlockedTemplate: resolvedUnlockedTemplate,
      upgradeTemplate: resolvedUpgradeTemplate,
      unlockedOffValue: transitionData.currentOffValue,
      transitionData,
      remainingData: {
        ...remainingData,
        value: transitionData.currentOffValue
          ? remainingData.remainingValue
          : remainingData.value,
      },
      extraTemplateData: {
        cart_count: cartDisplayData.value,
        cart_unit: cartDisplayData.unit,
      },
    });
  };

  const renderProgressPoints = (root, tiers, basis, metric, options = {}) => {
    const pointsContainer = root.querySelector(
      "[data-tiered-discount-progress-points]",
    );
    if (!pointsContainer) return;

    if (!tiers.length) {
      pointsContainer.innerHTML = "";
      return;
    }

    const max =
      options.maxOverride != null
        ? parseNumber(options.maxOverride, 0)
        : getTierThreshold(tiers[tiers.length - 1], basis);
    const valueType = String(options.valueType || "").toLowerCase() === "amount" ? "amount" : "percentage";
    const offLabel = options.offLabel || "off";
    pointsContainer.innerHTML = tiers
      .map((tier) => {
        const threshold = getTierThreshold(tier, basis);
        const position = Math.max(0, Math.min(100, (threshold / max) * 100));
        const active = metric >= threshold ? " is-active" : "";
        const thresholdLabel =
          basis === "amount" ? formatMoneyTiered(threshold) : `${tier.qty}+`;
        const tierValueLabel =
          valueType === "amount"
            ? `${formatMoneyTiered(parseNumber(tier.amountOffCents, 0))} ${offLabel}`
            : `${tier.percent}%`;
        const tooltipLabel = `${thresholdLabel} · ${tierValueLabel}`;
        return `<span class="tiered-discount-point${active}" style="left:${position}%" tabindex="0" role="button" aria-expanded="false" aria-label="${tooltipLabel}" data-tiered-discount-point><span class="tiered-discount-point__tooltip">${tooltipLabel}</span></span>`;
      })
      .join("");
  };

  const isMobileTooltipViewport = () =>
    window.matchMedia(MOBILE_TOOLTIP_MEDIA_QUERY).matches;

  const closeProgressTooltips = (scope = document) => {
    scope.querySelectorAll("[data-tiered-discount-point].is-tooltip-open").forEach((point) => {
      point.classList.remove("is-tooltip-open");
      point.setAttribute("aria-expanded", "false");
    });
  };

  const toggleProgressTooltip = (point) => {
    const container = point.closest("[data-tiered-discount-progress-points]");
    const willOpen = !point.classList.contains("is-tooltip-open");
    if (container) {
      closeProgressTooltips(container);
    }
    if (willOpen) {
      point.classList.add("is-tooltip-open");
      point.setAttribute("aria-expanded", "true");
    }
  };

  const createDetailEntryNode = (line) => {
    const item = document.createElement("div");
    item.className = "tiered-discount-cart__detail-item";

    const thumb = document.createElement("a");
    thumb.className = "tiered-discount-cart__detail-thumb";
    thumb.href = line.productUrl || "#";
    thumb.setAttribute("aria-hidden", line.productUrl ? "false" : "true");
    if (!line.productUrl) {
      thumb.tabIndex = -1;
    }

    if (line.imageSrc) {
      const img = document.createElement("img");
      img.loading = "lazy";
      img.src = line.imageSrc;
      img.alt = line.imageAlt || line.title || "";
      thumb.appendChild(img);
    }

    const body = document.createElement("div");
    body.className = "tiered-discount-cart__detail-body";

    const top = document.createElement("div");
    top.className = "tiered-discount-cart__detail-top";

    const title = document.createElement(line.productUrl ? "a" : "span");
    title.className = "tiered-discount-cart__detail-title";
    title.textContent = line.title || "";
    if (line.productUrl) {
      title.href = line.productUrl;
    }

    const prices = document.createElement("div");
    prices.className = "tiered-discount-cart__detail-prices";

    const original = document.createElement("span");
    original.className = "tiered-discount-cart__detail-price-original";
    original.textContent = formatMoneyTiered(line.unitPriceCents);

    const finalPrice = document.createElement("span");
    finalPrice.className = "tiered-discount-cart__detail-price-final";
    finalPrice.textContent = `${formatMoneyTiered(line.finalUnitPriceCents)} × ${line.quantity}`;

    if (line.unitPriceCents > line.finalUnitPriceCents) {
      prices.appendChild(original);
      prices.appendChild(finalPrice);
    } else {
      finalPrice.textContent = `${formatMoneyTiered(line.finalUnitPriceCents)} × ${line.quantity}`;
      prices.appendChild(finalPrice);
    }

    top.appendChild(title);
    top.appendChild(prices);

    const variant = document.createElement("div");
    variant.className = "tiered-discount-cart__detail-variant";
    variant.textContent = line.variantTitle || "";
    variant.hidden = !line.variantTitle;

    body.appendChild(top);
    body.appendChild(variant);

    item.appendChild(thumb);
    item.appendChild(body);

    return item;
  };

  const getCollapsedDetailsHeight = (list, itemCount) => {
    if (!list?.children?.length || itemCount <= 0) return 0;
    return 140;
  };

  const renderCartDetails = (root, state, solution, groupLineIndexes) => {
    const details = root.querySelector("[data-tiered-discount-details]");
    const list = root.querySelector("[data-tiered-discount-details-list]");
    const moreButton = root.querySelector("[data-tiered-discount-details-more]");
    if (!details || !list) return;

    const lines = (Array.isArray(groupLineIndexes) ? groupLineIndexes : [])
      .map((index) => state.rawLines[index] || solution.lineInfos[index])
      .filter((line) => line && line.id && line.id !== LOCAL_LINE_ID);

    if (!lines.length) {
      details.hidden = true;
      list.textContent = "";
      if (moreButton) {
        moreButton.hidden = true;
        moreButton.setAttribute("aria-expanded", "false");
      }
      return;
    }

    details.hidden = false;
    list.textContent = "";

    const expanded = root.dataset.tieredDiscountExpanded === "true";
    lines.forEach((line) => {
      list.appendChild(createDetailEntryNode(line));
    });

    const hasMore = lines.length > DEFAULT_VISIBLE_DETAIL_COUNT;
    if (hasMore) {
      const collapsedHeight = getCollapsedDetailsHeight(
        list,
        DEFAULT_VISIBLE_DETAIL_COUNT,
      );
      const expandedHeight = list.scrollHeight;
      list.style.maxHeight = `${expanded ? expandedHeight : collapsedHeight}px`;
    } else {
      list.style.maxHeight = `${list.scrollHeight}px`;
    }

    if (moreButton) {
      moreButton.hidden = !hasMore;
      moreButton.setAttribute("aria-expanded", expanded ? "true" : "false");
      if (expanded) {
        moreButton.hidden = true;
      }
    }
  };

  const resetDrawerBulkOffers = () => {
    document.querySelectorAll("[data-cart-bulk-offers]").forEach((module) => {
      module.dataset.cartBulkOffersState = "closed";
      module.setAttribute("aria-hidden", "true");
    });
  };

  const isBulkOffersOpen = (module) =>
    module?.dataset.cartBulkOffersState === "open";

  const setBulkOffersState = (module, open) => {
    if (!module) return;
    module.dataset.cartBulkOffersState = open ? "open" : "closed";
    module.setAttribute("aria-hidden", open ? "false" : "true");
  };

  const syncBulkOffersTriggers = () => {
    document.querySelectorAll("[data-cart-bulk-offers-trigger]").forEach((trigger) => {
      const scope = trigger.closest("[data-cart-bulk-offers-scope]");
      const module = scope?.querySelector("[data-cart-bulk-offers]");
      const roots = scope?.querySelectorAll("[data-tiered-discount-root]");
      const hasVisibleRoot = roots
        ? Array.from(roots).some((root) => !root.hidden)
        : false;
      if (module) {
        const hidden = !hasVisibleRoot;
        if (hidden) {
          setBulkOffersState(module, false);
        }
        module.hidden = hidden;
        module.setAttribute("aria-hidden", hidden ? "true" : "false");
        trigger.hidden = hidden;
      }
      const expanded = isBulkOffersOpen(module);
      trigger.setAttribute("aria-expanded", expanded ? "true" : "false");
    });
  };

  const tooltipObservers = new WeakMap();

  const positionTierTooltip = (root, ratio) => {
    const tooltip = root.querySelector("[data-tiered-tooltip]");
    const progressBar = root.querySelector("[role='progressbar']");
    if (!tooltip || !progressBar || tooltip.hidden) return;

    const barWidth = progressBar.clientWidth;
    const tooltipWidth = tooltip.offsetWidth;
    if (!barWidth || !tooltipWidth) return;

    const half = tooltipWidth / 2;
    const anchor = Math.max(0, Math.min(1, ratio)) * barWidth;
    const left = Math.max(half, Math.min(barWidth - half, anchor));
    tooltip.style.left = `${Math.round(left)}px`;
  };

  // The bubble lives inside a tab panel that may start hidden (width 0),
  // so re-position it whenever the progress bar gets its real width.
  const observeTierTooltip = (root) => {
    if (tooltipObservers.has(root) || typeof ResizeObserver === "undefined") return;
    const progressBar = root.querySelector("[role='progressbar']");
    if (!progressBar) return;
    const observer = new ResizeObserver(() => {
      const ratio = parseFloat(root.dataset.tieredTooltipRatio || "");
      if (!Number.isFinite(ratio)) return;
      positionTierTooltip(root, ratio);
    });
    observer.observe(progressBar);
    tooltipObservers.set(root, observer);
  };

  // Always-visible bubble: shows the next tier discount, pins to the max tier.
  const renderTierTooltip = (root, campaign, nextTier, maxOverride, state) => {
    const tooltip = root.querySelector("[data-tiered-tooltip]");
    if (!tooltip) return;

    const tiers = campaign.tiers || [];
    const tier = nextTier || tiers[tiers.length - 1];
    const offValue = tier ? getTierOffValue(campaign.valueType, tier) : "";
    if (!tier || !offValue) {
      tooltip.hidden = true;
      return;
    }

    const textNode = root.querySelector("[data-tiered-tooltip-text]");
    const offLabel = String(state?.labels?.tooltipOff || "").trim();
    if (textNode) {
      textNode.textContent = [offValue, offLabel].filter(Boolean).join(" ");
    }

    const threshold = getTierThreshold(tier, campaign.basis);
    const max = maxOverride || getTierThreshold(tiers[tiers.length - 1], campaign.basis);
    const ratio = max > 0 ? Math.max(0, Math.min(1, threshold / max)) : 0;
    root.dataset.tieredTooltipRatio = String(ratio);

    tooltip.hidden = false;
    observeTierTooltip(root);
    positionTierTooltip(root, ratio);
  };

  // Progress is the sum of every cart line eligible for this campaign,
  // independent of the discount optimizer assignment.
  const getCampaignMetricValue = (campaign, lines) =>
    (Array.isArray(lines) ? lines : []).reduce((sum, line) => {
      if (!Array.isArray(line.campaignIds) || !line.campaignIds.includes(campaign.discountId)) {
        return sum;
      }
      return sum + (
        campaign.basis === "amount"
          ? parseNumber(line.subtotalCents, 0)
          : parseNumber(line.quantity, 0)
      );
    }, 0);

  /**
   * Panel scope: render one resolved campaign without the cart optimizer,
   * so an empty cart still shows 0% progress plus the first-tier bubble.
   */
  const updateCampaignScopeRoot = (root, state) => {
    const displayCampaignId = String(state.displayCampaignId || "").trim();
    const campaignIndex = displayCampaignId
      ? state.campaigns.findIndex(
          (campaign) => String(campaign?.discountId || "").trim() === displayCampaignId,
        )
      : -1;
    if (campaignIndex < 0) {
      root.hidden = true;
      return;
    }

    const campaign = state.campaigns[campaignIndex];
    const metricValue = getCampaignMetricValue(campaign, state.rawLines);
    const maxOverride = getExtendedProgressMax(campaign.tiers, campaign.basis);
    const currentTier = getCurrentTier(campaign.tiers, campaign.basis, metricValue);
    const nextTier = getNextTier(campaign.tiers, campaign.basis, metricValue);
    const progress = getProgressRatio(campaign.tiers, campaign.basis, metricValue, {
      maxOverride,
    });

    const message = root.querySelector("[data-tiered-discount-message]");
    if (message) {
      message.textContent = getMessage({
        compact: state.compact,
        addTemplate: state.messages.addTemplate,
        unlockedTemplate: state.messages.unlockedTemplate,
        upgradeTemplate: state.messages.upgradeTemplate,
        itemLabelSingular: state.labels.itemLabelSingular,
        itemLabelPlural: state.labels.itemLabelPlural,
        valueType: campaign.valueType,
        basis: campaign.basis,
        metric: metricValue,
        currentTier,
        nextTier,
      });
    }

    const cartFill = root.querySelector("[data-tiered-discount-progress-fill-cart]");
    const preselectFill = root.querySelector(
      "[data-tiered-discount-progress-fill-preselect]",
    );
    if (cartFill) {
      cartFill.style.width = `${Math.round(progress * 100)}%`;
      cartFill.classList.remove("is-joined");
    }
    if (preselectFill) {
      preselectFill.hidden = true;
      preselectFill.style.width = "0";
    }

    const progressBar = root.querySelector("[role='progressbar']");
    if (progressBar) {
      progressBar.setAttribute("aria-label", state.progressLabel);
      progressBar.setAttribute("aria-valuenow", `${Math.round(progress * 100)}`);
    }

    renderProgressPoints(root, campaign.tiers, campaign.basis, metricValue, {
      maxOverride,
      valueType: campaign.valueType,
      offLabel: state.labels.tooltipOff,
    });
    renderTierTooltip(root, campaign, nextTier, maxOverride, state);
    root.hidden = false;
  };

  const buildRootState = (root) => {
    const dataNode = root.querySelector("[data-tiered-discount-data]");
    if (!dataNode) return null;

    const campaigns = normalizeCampaigns(
      parseJsonScript(root, "[data-tiered-discount-campaigns]", []),
      { includeTitleMessage: true },
    );
    if (!campaigns.length) return null;

    const cartLines = normalizeLines(
      parseJsonScript(root, "[data-tiered-discount-cart-lines]", []),
    );
    const context = dataNode.dataset.tieredDiscountContext || "product";
    const displayCampaignId =
      (dataNode.dataset.tieredDiscountDisplayCampaignId || "").trim();
    const parsedLocalCampaignIds = parseJson(
      dataNode.dataset.tieredDiscountLocalCampaignIds || "[]",
      [],
    );
    const localCampaignIds = Array.isArray(parsedLocalCampaignIds)
      ? parsedLocalCampaignIds
          .map((discountId) => String(discountId || "").trim())
          .filter(Boolean)
      : [];
    const initialLocalQty = parseNumber(
      dataNode.dataset.tieredDiscountLocalQty,
      1,
    );
    const localQty =
      context === "cart"
        ? 0
        : parseNumber(
            root
              .closest("[data-tiered-discount-scope]")
              ?.querySelector('input[name="quantity"]')?.value,
            initialLocalQty,
          );
    const basePrice = parseNumber(dataNode.dataset.tieredDiscountBasePrice, 0);
    const baseCompareAtPrice = parseNumber(
      dataNode.dataset.tieredDiscountBaseCompareAtPrice,
      0,
    );
    const rawLines = [...cartLines];

    if (context !== "cart" && localQty > 0 && localCampaignIds.length) {
      rawLines.push({
        id: LOCAL_LINE_ID,
        quantity: localQty,
        subtotalCents: basePrice * localQty,
        campaignIds: localCampaignIds,
      });
    }

    return {
      dataNode,
      campaigns,
      context,
      compact: root.dataset.tieredDiscountCompact === "true",
      displayCampaignId,
      localCampaignIds,
      localQty,
      basePrice,
      baseCompareAtPrice,
      rawLines,
      messages: {
        addTemplate: decodeHtmlEntities(dataNode.dataset.tieredDiscountMessageAdd || ""),
        unlockedTemplate: decodeHtmlEntities(
          dataNode.dataset.tieredDiscountMessageUnlocked || "",
        ),
        upgradeTemplate: decodeHtmlEntities(
          dataNode.dataset.tieredDiscountMessageUpgrade || "",
        ),
        pdp: {
          cartAdd: decodeHtmlEntities(
            dataNode.dataset.tieredDiscountMessagePdpCartAdd || "",
          ),
          cartUpgrade: decodeHtmlEntities(
            dataNode.dataset.tieredDiscountMessagePdpCartUpgrade || "",
          ),
          cartMax: decodeHtmlEntities(
            dataNode.dataset.tieredDiscountMessagePdpCartMax || "",
          ),
        },
      },
      labels: {
        itemLabelSingular: decodeHtmlEntities(
          dataNode.dataset.tieredDiscountItemLabelSingular || "",
        ),
        itemLabelPlural: decodeHtmlEntities(
          dataNode.dataset.tieredDiscountItemLabelPlural || "",
        ),
        tooltipOff: decodeHtmlEntities(
          dataNode.dataset.tieredDiscountTooltipOffLabel || "",
        ),
      },
      progressLabel: decodeHtmlEntities(dataNode.dataset.tieredDiscountProgressLabel || ""),
      moreUrl: decodeHtmlEntities(dataNode.dataset.tieredDiscountMoreUrl || ""),
    };
  };

  const updateRoot = async (root) => {
    await fetchLatestRootPayload(root);
    const state = buildRootState(root);
    if (!state) {
      root.hidden = true;
      return;
    }

    // Campaign-scoped roots (the promotion panel) resolve their own campaign
    // and stay visible with an empty cart.
    if (root.dataset.tieredDiscountDisplayScope === "campaign") {
      updateCampaignScopeRoot(root, state);
      return;
    }

    if (!state.rawLines.length) {
      root.hidden = true;
      return;
    }

    const solution = optimizeDiscountAssignments(
      state.campaigns,
      state.rawLines,
      state.context === "cart"
        ? {}
        : { mode: "single_best", requiredLineId: LOCAL_LINE_ID },
    );
    const resolved = resolveProductDisplayCampaign(solution, {
      displayCampaignId: state.displayCampaignId,
      localLineId: LOCAL_LINE_ID,
      localQty: state.localQty,
      basePrice: state.basePrice,
    });

    if (!resolved || !resolved.campaign || !resolved.campaign.tiers.length) {
      root.hidden = true;
      return;
    }

    const { campaign, metricValue, currentTier, nextTier, groupLineIndexes } =
      resolved;
    const maxOverride = getExtendedProgressMax(campaign.tiers, campaign.basis);
    const campaignIndex = state.campaigns.findIndex(
      (c) => String(c?.discountId || "").trim() === String(campaign.discountId || "").trim(),
    );
    const applicableLineIndexes =
      state.context !== "cart" && campaignIndex >= 0
        ? solution.lineInfos
            .map((line, index) =>
              line?.applicableCampaignIndexes?.includes(campaignIndex) ? index : -1,
            )
            .filter((index) => index >= 0)
        : Array.isArray(groupLineIndexes)
          ? groupLineIndexes
          : [];
    const displayMetricValue =
      state.context !== "cart"
        ? applicableLineIndexes.reduce((sum, lineIndex) => {
            const line = solution.lineInfos[lineIndex];
            return sum + (
              campaign.basis === "amount"
                ? parseNumber(line?.subtotalCents, 0)
                : parseNumber(line?.quantity, 0)
            );
          }, 0)
        : metricValue;
    const displayCurrentTier =
      typeof getCurrentTier === "function"
        ? getCurrentTier(campaign.tiers, campaign.basis, displayMetricValue)
        : currentTier;
    const displayNextTier =
      typeof getNextTier === "function"
        ? getNextTier(campaign.tiers, campaign.basis, displayMetricValue)
        : nextTier;

    const localLineIndex = solution.lineInfos.findIndex((line) => line.id === LOCAL_LINE_ID);
    const localMetric = localLineIndex >= 0
      ? campaign.basis === "amount"
        ? parseNumber(solution.lineInfos[localLineIndex]?.subtotalCents, 0)
        : parseNumber(solution.lineInfos[localLineIndex]?.quantity, 0)
      : 0;
    const cartMetricValue = Math.max(0, displayMetricValue - localMetric);
    const cartItemCount = applicableLineIndexes.reduce((sum, lineIndex) => {
      const line = solution.lineInfos[lineIndex];
      if (!line || line.id === LOCAL_LINE_ID) return sum;
      return sum + parseNumber(line.quantity, 0);
    }, 0);
    const cartCurrentTier =
      typeof getCurrentTier === "function"
        ? getCurrentTier(campaign.tiers, campaign.basis, cartMetricValue)
        : displayCurrentTier;
    const cartNextTier =
      typeof getNextTier === "function"
        ? getNextTier(campaign.tiers, campaign.basis, cartMetricValue)
        : displayNextTier;

    const progress = getProgressRatio(campaign.tiers, campaign.basis, displayMetricValue, {
      maxOverride,
    });
    const title = root.querySelector("[data-tiered-discount-title-text]");
    const message = root.querySelector("[data-tiered-discount-message]");
    const more = root.querySelector("[data-tiered-discount-more]");
    const progressFill = root.querySelector("[data-tiered-discount-progress-fill]");
    const cartFill = root.querySelector("[data-tiered-discount-progress-fill-cart]");
    const preselectFill = root.querySelector(
      "[data-tiered-discount-progress-fill-preselect]",
    );

    if (title) {
      title.textContent = campaign.discountTitle || "";
    }

    if (message) {
      const pdpMessage = getPdpMessage({
        context: state.context,
        valueType: campaign.valueType,
        basis: campaign.basis,
        addTemplate: state.messages.addTemplate,
        unlockedTemplate: state.messages.unlockedTemplate,
        upgradeTemplate: state.messages.upgradeTemplate,
        pdpTemplates: state.messages.pdp,
        cartMetric: cartMetricValue,
        cartCount: cartItemCount,
        itemLabelSingular: state.labels.itemLabelSingular,
        itemLabelPlural: state.labels.itemLabelPlural,
        totalMetric: displayMetricValue,
        totalCurrentTier: displayCurrentTier,
        totalNextTier: displayNextTier,
        cartCurrentTier,
        cartNextTier,
      });
      message.textContent = pdpMessage || getMessage({
        compact: state.compact,
        addTemplate: state.messages.addTemplate,
        unlockedTemplate: state.messages.unlockedTemplate,
        upgradeTemplate: state.messages.upgradeTemplate,
        itemLabelSingular: state.labels.itemLabelSingular,
        itemLabelPlural: state.labels.itemLabelPlural,
        valueType: campaign.valueType,
        basis: campaign.basis,
        metric: cartMetricValue,
        currentTier: cartCurrentTier,
        nextTier: cartNextTier,
      });
    }

    if (more) {
      const url = String(state.moreUrl || "").trim();
      if (url) {
        more.href = url;
        more.hidden = false;
      } else {
        more.hidden = true;
      }
    }

    if (cartFill && preselectFill) {
      const indexes = applicableLineIndexes;
      const localLineIndex = solution.lineInfos.findIndex((line) => line.id === LOCAL_LINE_ID);
      const localIncluded =
        localLineIndex >= 0 ? indexes.includes(localLineIndex) : false;
      const localLine = localIncluded ? solution.lineInfos[localLineIndex] : null;
      const localMetric = localLine
        ? campaign.basis === "amount"
          ? parseNumber(localLine.subtotalCents, 0)
          : parseNumber(localLine.quantity, 0)
        : 0;
      const cartMetric = Math.max(0, displayMetricValue - localMetric);
      const cartRatio = getProgressRatio(campaign.tiers, campaign.basis, cartMetric, {
        maxOverride,
      });
      const splitRatio = Math.max(0, progress - cartRatio);

      cartFill.style.width = `${Math.round(cartRatio * 100)}%`;
      preselectFill.style.left = `${Math.round(cartRatio * 100)}%`;
      preselectFill.style.width = `${Math.round(splitRatio * 100)}%`;
      preselectFill.hidden = splitRatio <= 0;

      const hasCartFill = cartRatio > 0;
      const hasPreselectFill = splitRatio > 0;
      cartFill.classList.toggle(
        "is-joined",
        hasCartFill && hasPreselectFill,
      );
      preselectFill.classList.toggle(
        "is-joined",
        hasCartFill && hasPreselectFill,
      );
    } else if (progressFill) {
      progressFill.style.width = `${Math.round(progress * 100)}%`;
    }

    const progressBar = root.querySelector("[role='progressbar']");
    if (progressBar) {
      progressBar.setAttribute("aria-label", state.progressLabel);
      progressBar.setAttribute("aria-valuenow", `${Math.round(progress * 100)}`);
    }

    renderProgressPoints(root, campaign.tiers, campaign.basis, displayMetricValue, {
      maxOverride,
      valueType: campaign.valueType,
      offLabel: state.labels.tooltipOff,
    });
    renderCartDetails(root, state, solution, groupLineIndexes);
    root.hidden = false;
  };

  const updateAll = async () => {
    await Promise.all(
      Array.from(document.querySelectorAll(ROOT_SELECTOR)).map((root) => updateRoot(root)),
    );
    syncBulkOffersTriggers();
  };

  const handleDocumentClick = (event) => {
    const progressPoint = event.target.closest("[data-tiered-discount-point]");
    if (progressPoint && isMobileTooltipViewport()) {
      toggleProgressTooltip(progressPoint);
      return;
    }

    if (isMobileTooltipViewport()) {
      closeProgressTooltips();
    }

    const moreButton = event.target.closest("[data-tiered-discount-details-more]");
    if (moreButton) {
      const root = moreButton.closest(ROOT_SELECTOR);
      if (!root) return;
      root.dataset.tieredDiscountExpanded = "true";
      updateRoot(root);
      return;
    }

    const closeButton = event.target.closest("[data-cart-bulk-offers-close]");
    if (closeButton) {
      const module = closeButton.closest("[data-cart-bulk-offers]");
      if (module) {
        setBulkOffersState(module, false);
        syncBulkOffersTriggers();
      }
      return;
    }

    const trigger = event.target.closest("[data-cart-bulk-offers-trigger]");
    if (!trigger) return;
    const scope = trigger.closest("[data-cart-bulk-offers-scope]");
    const module = scope?.querySelector("[data-cart-bulk-offers]");
    if (module) {
      const willOpen = !isBulkOffersOpen(module);
      setBulkOffersState(module, willOpen);
      syncBulkOffersTriggers();
      if (willOpen) {
        window.setTimeout(() => {
          module.scrollIntoView({ behavior: "smooth", block: "nearest" });
        }, 120);
      }
    }
  };

  const handleCartDrawerOpen = () => {
    resetDrawerBulkOffers();
    document.querySelectorAll(ROOT_SELECTOR).forEach((root) => {
      delete root.dataset.tieredDiscountExpanded;
    });
    updateAll();
  };

  const handleCartDrawerClose = () => {
    updateAll();
  };

  const handleCartChanged = () => {
    markCartChanged();
    updateAll();
  };

  document.addEventListener("DOMContentLoaded", updateAll);
  document.addEventListener("click", handleDocumentClick);
  document.addEventListener("quick-view:open", updateAll);
  document.addEventListener("product:section-rendered", updateAll);
  document.addEventListener("quantity:change", updateAll);
  document.addEventListener("cart:item-added", handleCartChanged);
  document.addEventListener("cart-drawer:open", handleCartDrawerOpen);
  document.addEventListener("cart-drawer:close", handleCartDrawerClose);
  document.addEventListener("line-item:change:end", handleCartChanged);
})();
